/**
 * Contract Package Import — the pre-wired Contract -> Project -> BidItem -> LineItem entity graph and the
 * pure builders that turn the bespoke view's entity-aware choices (which source column keys each level,
 * which levels are picked-constants vs created-from-data) into the exact config the shared engine consumes:
 *
 *   - a meadow-integration GUID *strategy config*  (compileGUIDStrategy / DataImport-GuidStrategyApply)
 *   - a dataimport *mapping*  (Order + Entities{} with per-entity Mappings + GUIDName + attached strategy)
 *   - a push *context*  (Order, per-entity GUID column sizes, marshal prefixes)
 *
 * This is the ONE Headlight-specific piece of the bespoke importer — the composer, strategy compiler,
 * comprehension builder and ordered multi-entity push all live in the modules (meadow-integration +
 * pict-section-dataimport) and are reused verbatim. Pure data + functions; no pict / REST / DOM here.
 *
 * The FK graph (verified against lib/headlight_model/json/Headlight.json — every level carries a DIRECT
 * foreign key to each ancestor, which is exactly what the context-aware GUID composer wants):
 *
 *     Contract (IDContract / GUIDContract)                     — root
 *        └─ Project   (IDContract)                             — Project.IDContract -> Contract
 *              └─ BidItem  (IDContract, IDProject)             — direct to both ancestors
 *                    └─ LineItem (IDContract, IDProject, IDBidItem)   — direct to all three
 *
 * How a child's foreign key to an ancestor is landed, chosen per-level by the scope step:
 *   - CREATED ancestor -> a same-upload GUID *join* (`GUID<Ancestor>`): the child carries the ancestor's
 *     COMPOSED GUID and the ordered push resolves it to the real id after the ancestor is created. The
 *     join recomputes the ancestor's FULL GUID (its own ancestors included) so it matches byte-for-byte.
 *   - PICKED ancestor -> a *constant* foreign key (`ID<Ancestor>` = the picked row id) stamped on every
 *     child as a literal mapping; the ancestor is NOT created. Its natural key still rides a synthetic
 *     constant column into each descendant GUID as a context segment, so re-imports stay idempotent.
 */

'use strict';

// The GUID marshal prefix for the whole package. The strategy composes the entire GUID (e.g.
// `UI_C10_P01278_LI8675309`), so this is the composer's prefix, not an adapter marshal prefix.
const PACKAGE_GUID_PREFIX = 'UI';

/**
 * The ordered Contract-package levels, root-first (this IS the push Order). Each declares:
 *   - Abbrev:      the STABLE GUID segment abbreviation (load-bearing — same data must yield same GUID).
 *   - GUIDName:    the entity's GUID column.
 *   - Parents:     ancestors this level carries a direct FK to (root-first).
 *   - ForeignKey:  ID<Parent> column name per parent (all direct here).
 *   - DefaultKeyColumns: sensible default natural-key columns the view pre-selects (user can change).
 *   - GUIDSize:    fallback GUID column width (the live schema width wins at runtime).
 */
const PACKAGE_ENTITIES =
[
	{
		Entity: 'Contract', Abbrev: 'C', GUIDName: 'GUIDContract',
		Label: 'Contract', Icon: 'fas fa-file-signature',
		Parents: [],
		ForeignKey: {},
		DefaultKeyColumns: [ 'PrimaryProjectNumber', 'Name' ],
		// Normalized (lowercased, alphanumeric-only) substrings the auto-seed prefers, in order, when
		// picking the key column from realistic headers ("Contract Number", "Contract No", …).
		KeyHints: [ 'contractnumber', 'contractno', 'primaryprojectnumber', 'contractid', 'contract' ],
		GUIDSize: 64,
	},
	{
		Entity: 'Project', Abbrev: 'P', GUIDName: 'GUIDProject',
		Label: 'Project', Icon: 'fas fa-diagram-project',
		Parents: [ 'Contract' ],
		ForeignKey: { Contract: 'IDContract' },
		DefaultKeyColumns: [ 'Name' ],
		KeyHints: [ 'projectnumber', 'projectname', 'projectcode', 'projectid', 'project' ],
		GUIDSize: 64,
	},
	{
		Entity: 'BidItem', Abbrev: 'BI', GUIDName: 'GUIDBidItem',
		Label: 'Bid Item', Icon: 'fas fa-list-ol',
		Parents: [ 'Contract', 'Project' ],
		ForeignKey: { Contract: 'IDContract', Project: 'IDProject' },
		DefaultKeyColumns: [ 'Name' ],
		KeyHints: [ 'biditemnumber', 'biditemname', 'biditem', 'bidgroup', 'sectionname' ],
		GUIDSize: 64,
	},
	{
		Entity: 'LineItem', Abbrev: 'LI', GUIDName: 'GUIDLineItem',
		Label: 'Line Item', Icon: 'fas fa-list-check',
		Parents: [ 'Contract', 'Project', 'BidItem' ],
		ForeignKey: { Contract: 'IDContract', Project: 'IDProject', BidItem: 'IDBidItem' },
		DefaultKeyColumns: [ 'ItemNumber', 'Name' ],
		KeyHints: [ 'itemnumber', 'lineitemnumber', 'linenumber', 'itemno', 'itemcode' ],
		GUIDSize: 64,
	},
];

const ENTITY_ORDER = PACKAGE_ENTITIES.map((pEntity) => pEntity.Entity);

/** Look up a package level by entity name. @param {string} pEntity @returns {object|null} */
function findLevel(pEntity)
{
	return PACKAGE_ENTITIES.find((pLevel) => pLevel.Entity === pEntity) || null;
}

/** The synthetic per-row column that carries a PICKED ancestor's natural key into descendant GUIDs. */
function syntheticKeyColumn(pParentEntity)
{
	return `__PKG_${pParentEntity}Key`;
}

/**
 * The context catalog (Abbrev / GUIDSize per entity) the GUID strategy compiler consumes. Built from the
 * package graph so the abbreviations always agree with PACKAGE_ENTITIES.
 * @returns {Record<string,{Abbrev:string, KeyField:string, GUIDSize:number}>}
 */
function contextCatalog()
{
	const tmpCatalog = {};
	PACKAGE_ENTITIES.forEach((pLevel) =>
	{
		tmpCatalog[pLevel.Entity] = { Abbrev: pLevel.Abbrev, KeyField: pLevel.DefaultKeyColumns[0], GUIDSize: pLevel.GUIDSize };
	});
	return tmpCatalog;
}

/**
 * Normalize the per-level scope + key selections the view collected into a canonical plan the builders
 * consume. Defensive about missing pieces so a partially-filled UI never throws mid-build.
 *
 * @param {object} pScope
 * @param {Record<string,'create'|'pick'>} pScope.Modes            per-entity: 'create' | 'pick'
 * @param {Record<string,number>}          [pScope.PickedIDs]      picked row id per 'pick' entity (constant FK)
 * @param {Record<string,string>}          [pScope.PickedKeys]     picked parent natural key per 'pick' entity
 *                                                                 (a constant GUID context segment)
 * @param {Record<string,string[]>}        [pScope.KeyColumns]     per created entity: source column(s) that key it
 *                                                                 (v1 uses the first — a single key keeps joins matched)
 * @param {Record<string,string>}          [pScope.ParentKeyColumns] "<Child>.<Parent>" -> the source column in the
 *                                                                 child's rows that identifies that created ancestor
 * @returns {{Levels: Array<object>, Order: string[]}}
 */
function normalizePlan(pScope)
{
	const tmpScope = pScope || {};
	const tmpModes = tmpScope.Modes || {};
	const tmpPickedIDs = tmpScope.PickedIDs || {};
	const tmpPickedKeys = tmpScope.PickedKeys || {};
	const tmpKeyColumns = tmpScope.KeyColumns || {};
	const tmpParentKeyColumns = tmpScope.ParentKeyColumns || {};
	const tmpReferenceKeyColumns = tmpScope.ReferenceKeyColumns || {};

	const tmpLevels = PACKAGE_ENTITIES.map((pLevel) =>
	{
		const tmpMode = (tmpModes[pLevel.Entity] === 'pick') ? 'pick'
			: (tmpModes[pLevel.Entity] === 'reference') ? 'reference' : 'create';
		const tmpKeys = Array.isArray(tmpKeyColumns[pLevel.Entity])
			? tmpKeyColumns[pLevel.Entity].filter((pColumn) => !!pColumn)
			: [];
		return Object.assign({}, pLevel,
			{
				Mode: tmpMode,
				KeyColumns: (tmpKeys.length > 0) ? tmpKeys : pLevel.DefaultKeyColumns.slice(0, 1),
				PickedID: tmpPickedIDs[pLevel.Entity],
				PickedKey: tmpPickedKeys[pLevel.Entity],
				// The source column in THIS level's rows that identifies each created ancestor (giant sheet:
				// usually the ancestor's own key column; the view can override per Child.Parent pair).
				ParentKeyColumn: pLevel.Parents.reduce((pMap, pParent) =>
				{
					pMap[pParent] = tmpParentKeyColumns[`${pLevel.Entity}.${pParent}`] || '';
					return pMap;
				}, {}),
			});
	});

	// Only levels the user chose to create are pushed. A picked level drops out of creation (it already
	// exists) — its identity flows to descendants as a constant FK + constant GUID context segment.
	const tmpOrder = tmpLevels.filter((pLevel) => pLevel.Mode === 'create').map((pLevel) => pLevel.Entity);

	return { Levels: tmpLevels, Order: tmpOrder, ReferenceKeyColumns: tmpReferenceKeyColumns };
}

/** The per-row column carrying a referenced parent's resolved ID (many-project items import). */
function resolvedIDColumn(pParentEntity)
{
	return `__PKG_ID${pParentEntity}`;
}

/** The mode ('create' | 'pick') of a level within a plan. */
function _levelMode(pPlan, pEntity)
{
	const tmpLevel = pPlan.Levels.find((pLevel) => pLevel.Entity === pEntity);
	return tmpLevel ? tmpLevel.Mode : 'create';
}

/**
 * The source column in a CHILD level's rows that identifies an ANCESTOR — the single knob every context
 * segment + join key reuses, so the child's recomputed ancestor GUID matches the ancestor's own GUID:
 *   - picked ancestor  -> its synthetic constant column (carries the picked natural key on every row)
 *   - created ancestor -> the child's explicit Child.Parent override, else the ancestor's own key column
 *     (in a giant denormalized sheet these are the same physical column)
 */
function _childColumnForAncestor(pChildLevel, pAncestor, pPlan)
{
	if (_levelMode(pPlan, pAncestor) === 'pick')
	{
		return syntheticKeyColumn(pAncestor);
	}
	if (_levelMode(pPlan, pAncestor) === 'reference')
	{
		// An existing parent matched per-row by a real key column in the data (many-project items import).
		return (pPlan.ReferenceKeyColumns || {})[pAncestor] || '';
	}
	// The ancestor's own key column comes from the NORMALIZED plan level (KeyColumns), not the raw catalog
	// entry (which only carries DefaultKeyColumns).
	const tmpAncestorLevel = pPlan.Levels.find((pLevel) => pLevel.Entity === pAncestor);
	return pChildLevel.ParentKeyColumn[pAncestor] || (tmpAncestorLevel ? tmpAncestorLevel.KeyColumns[0] : '');
}

/**
 * Build the meadow-integration GUID strategy config for a normalized plan. Each created level composes its
 * GUID from every ancestor's key segment (context) + its own single key, and carries a same-upload GUID
 * join for every CREATED ancestor — the join carrying the ancestor's own grandparent context so the
 * recomputed `GUID<Ancestor>` matches the ancestor's own GUID byte-for-byte. PICKED ancestors are context
 * segments only (via their synthetic constant column); their FK is landed as a constant ID by buildMapping.
 *
 * @param {{Levels:Array<object>, Order:string[]}} pPlan
 * @returns {{Prefix:string, Entities:Record<string,object>}}
 */
function buildStrategyConfig(pPlan)
{
	const tmpPlan = pPlan || normalizePlan({});
	const tmpEntities = {};

	tmpPlan.Levels.forEach((pLevel) =>
	{
		if (pLevel.Mode !== 'create')
		{
			return; // picked levels aren't composed / created
		}

		// EVERY ancestor (created or picked) contributes a context segment, keyed by the child's column
		// for that ancestor (a real column, or the picked ancestor's synthetic constant column).
		const tmpContextEntities = pLevel.Parents.slice(); // already root-first
		const tmpContextKeyColumns = {};
		tmpContextEntities.forEach((pParent) =>
		{
			tmpContextKeyColumns[pParent] = _childColumnForAncestor(pLevel, pParent, tmpPlan);
		});

		// Joins (FK GUID fields) only for CREATED ancestors. Each join recomputes the ancestor's full GUID,
		// so it carries the ancestor's OWN ancestors as grandparent context (keyed by the child's columns).
		const tmpJoins = pLevel.Parents
			.filter((pParent) => _levelMode(tmpPlan, pParent) === 'create')
			.map((pParent) =>
			{
				const tmpParentLevel = findLevel(pParent);
				const tmpGrandContext = (tmpParentLevel ? tmpParentLevel.Parents : []).slice();
				const tmpGrandKeyColumns = {};
				tmpGrandContext.forEach((pGrand) =>
				{
					tmpGrandKeyColumns[pGrand] = _childColumnForAncestor(pLevel, pGrand, tmpPlan);
				});
				return {
					ParentEntity: pParent,
					Mode: 'prefixed',
					KeyColumn: _childColumnForAncestor(pLevel, pParent, tmpPlan),
					ContextEntities: tmpGrandContext,
					ContextKeyColumns: tmpGrandKeyColumns,
					CrossSession: false, // same upload — resolved from the ordered push batch's GUIDMap
				};
			});

		tmpEntities[pLevel.Entity] =
		{
			Mode: 'prefixed',
			OwnKeyColumn: pLevel.KeyColumns[0], // single key column — keeps the join recompute matched
			ContextEntities: tmpContextEntities,
			ContextKeyColumns: tmpContextKeyColumns,
			Joins: tmpJoins,
		};
	});

	return { Prefix: PACKAGE_GUID_PREFIX, Entities: tmpEntities };
}

/**
 * Build the dataimport mapping (Order + Entities{}) for a normalized plan + the per-entity column->field
 * assignments the map step produced. Constant foreign keys (picked ancestors) are injected as literal
 * mapping values; created-ancestor FKs are left to the GUID join (attached later by applyStrategy).
 *
 * @param {{Levels:Array<object>, Order:string[]}} pPlan
 * @param {Record<string, Record<string,string>>} pFieldAssignments  entity -> { targetField: sourceColumn }
 * @returns {{Order:string[], Entities:Record<string,object>}}
 */
function buildMapping(pPlan, pFieldAssignments)
{
	const tmpPlan = pPlan || normalizePlan({});
	const tmpAssignments = pFieldAssignments || {};
	const tmpEntities = {};

	tmpPlan.Levels.forEach((pLevel) =>
	{
		if (pLevel.Mode !== 'create')
		{
			return;
		}

		const tmpAssigned = tmpAssignments[pLevel.Entity] || {};
		const tmpMappings = {};

		// Real data columns the user mapped: value is a pict template pulling the source column.
		Object.keys(tmpAssigned).forEach((pTargetField) =>
		{
			const tmpSourceColumn = tmpAssigned[pTargetField];
			if (tmpSourceColumn)
			{
				tmpMappings[pTargetField] = `{~D:Record.${tmpSourceColumn}~}`;
			}
		});

		// Foreign keys for non-created ancestors:
		//   pick      -> a literal constant id (the picked parent), passed through verbatim.
		//   reference -> `{~D:Record.__PKG_ID<Parent>~}`, the per-row id the provider resolved by natural key.
		// (created ancestors carry no ID<Parent> mapping — their FK is the strategy's GUID join.)
		pLevel.Parents.forEach((pParent) =>
		{
			const tmpFKColumn = pLevel.ForeignKey[pParent];
			if (!tmpFKColumn) { return; }
			const tmpParentMode = _levelMode(tmpPlan, pParent);
			if (tmpParentMode === 'pick')
			{
				const tmpParentLevel = tmpPlan.Levels.find((pOther) => pOther.Entity === pParent);
				if (tmpParentLevel && (tmpParentLevel.PickedID != null)) { tmpMappings[tmpFKColumn] = String(tmpParentLevel.PickedID); }
			}
			else if (tmpParentMode === 'reference')
			{
				tmpMappings[tmpFKColumn] = `{~D:Record.${resolvedIDColumn(pParent)}~}`;
			}
		});

		tmpEntities[pLevel.Entity] = { Entity: pLevel.Entity, GUIDName: pLevel.GUIDName, Mappings: tmpMappings };
	});

	return { Order: tmpPlan.Order.slice(), Entities: tmpEntities };
}

/**
 * Add the synthetic constant columns a plan's PICKED ancestors need (their natural key on every row) so
 * the GUID composer can fold them into descendant GUIDs. Returns a new row array (originals untouched);
 * a no-op (same array) when nothing is picked.
 * @param {Array<Record<string,any>>} pRows @param {{Levels:Array<object>}} pPlan
 * @returns {Array<Record<string,any>>}
 */
function injectConstantColumns(pRows, pPlan)
{
	const tmpPlan = pPlan || normalizePlan({});
	const tmpPicked = tmpPlan.Levels.filter((pLevel) => (pLevel.Mode === 'pick') && (pLevel.PickedKey != null) && (String(pLevel.PickedKey) !== ''));
	if (tmpPicked.length === 0)
	{
		return Array.isArray(pRows) ? pRows : [];
	}
	return (Array.isArray(pRows) ? pRows : []).map((pRow) =>
	{
		const tmpRow = Object.assign({}, pRow);
		tmpPicked.forEach((pLevel) => { tmpRow[syntheticKeyColumn(pLevel.Entity)] = String(pLevel.PickedKey); });
		return tmpRow;
	});
}

/**
 * The push context the shared _pushViaHeadlight seam consumes: ordered created entities, per-entity GUID
 * column widths (so a composed GUID isn't re-truncated), and the explicitly-empty marshal prefixes that
 * tell the adapter to store the strategy-composed GUID verbatim.
 * @param {{Order:string[]}} pPlan
 * @param {Record<string,number>} [pGUIDSizes]  entity -> GUID column width (from the resolved schema)
 * @returns {{Order:string[], GUIDPrefix:string, EntityGUIDPrefix:string, AllowGUIDTruncation:boolean, GUIDColumnSizes:Record<string,number>}}
 */
function buildPushContext(pPlan, pGUIDSizes)
{
	const tmpPlan = pPlan || normalizePlan({});
	const tmpSizes = pGUIDSizes || {};
	const tmpColumnSizes = {};
	tmpPlan.Order.forEach((pEntity) =>
	{
		const tmpLevel = findLevel(pEntity);
		tmpColumnSizes[pEntity] = tmpSizes[pEntity] || (tmpLevel ? tmpLevel.GUIDSize : 64);
	});
	return {
		Order: tmpPlan.Order.slice(),
		// Empty marshal prefixes: the GUID strategy already composed the full `UI_...` GUID; the adapter
		// must store it verbatim (a non-empty prefix would double-marshal + break cross-level FK matching).
		GUIDPrefix: '',
		EntityGUIDPrefix: '',
		AllowGUIDTruncation: true,
		GUIDColumnSizes: tmpColumnSizes,
	};
}

module.exports =
{
	PACKAGE_GUID_PREFIX,
	PACKAGE_ENTITIES,
	ENTITY_ORDER,
	findLevel,
	syntheticKeyColumn,
	resolvedIDColumn,
	contextCatalog,
	normalizePlan,
	buildStrategyConfig,
	buildMapping,
	injectConstantColumns,
	buildPushContext,
};
